import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// User activity entry
export interface UserActivityEntry {
  user_id: string;
  email: string | null;
  full_name: string | null;
  reports_count: number;
  dictation_minutes: number;
  last_active: string | null;
  status: string;
}

// Activity summary stats
export interface ActivitySummary {
  dau: number; // Daily active users
  wau: number; // Weekly active users
  mau: number; // Monthly active users
  total_reports_7d: number;
  total_dictation_minutes_7d: number;
  avg_reports_per_user: number;
}

// Activity trend data point
export interface ActivityTrendPoint {
  date: string;
  active_users: number;
  reports: number;
}

interface ActivityFilters {
  startDate?: Date;
  endDate?: Date;
  status?: string;
}

interface UseUserActivityResult {
  users: UserActivityEntry[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  fetch: (filters?: ActivityFilters, page?: number) => Promise<void>;
}

interface UseActivitySummaryResult {
  summary: ActivitySummary | null;
  loading: boolean;
  fetch: () => Promise<void>;
}

interface UseActivityTrendResult {
  trend: ActivityTrendPoint[];
  loading: boolean;
  fetch: (days?: number) => Promise<void>;
}

/**
 * Hook for fetching user activity list
 */
export function useUserActivity(): UseUserActivityResult {
  const [users, setUsers] = useState<UserActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (filters?: ActivityFilters, pageNum: number = 1) => {
    setLoading(true);
    try {
      // Try RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_get_user_activity', {
        p_start_date: filters?.startDate?.toISOString() || null,
        p_end_date: filters?.endDate?.toISOString() || null
      });

      if (rpcError) {
        // If RPC doesn't exist, build from accounts table
        if (rpcError.code === '42883') {
          console.warn('admin_get_user_activity RPC not found - falling back to direct table access');

          // Query profiles first (has id, email, full_name)
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, email, full_name');

          // Create profiles lookup map
          const profilesMap = new Map(
            (profilesData || []).map(p => [p.id, p])
          );

          // Query accounts
          let query = supabase
            .from('accounts')
            .select(`
              user_id,
              status,
              reports_used,
              dictation_minutes_used,
              last_active
            `, { count: 'exact' })
            .order('last_active', { ascending: false, nullsFirst: false })
            .range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

          if (filters?.status) {
            query = query.eq('status', filters.status);
          }

          const { data, error, count } = await query;

          if (error) throw error;

          // Map data
          const mappedUsers = (data || []).map((acc: { user_id: string; status: string; reports_used: number | null; dictation_minutes_used: number | null; last_active: string | null }) => {
            const profile = profilesMap.get(acc.user_id);
            return {
              user_id: acc.user_id,
              email: profile?.email || null,
              full_name: profile?.full_name || null,
              reports_count: acc.reports_used || 0,
              dictation_minutes: acc.dictation_minutes_used || 0,
              last_active: acc.last_active,
              status: acc.status
            };
          }) as UserActivityEntry[];

          setUsers(mappedUsers);
          setTotal(count || 0);
          setPage(pageNum);
          return;
        }
        throw rpcError;
      }

      // Parse RPC result
      const result = rpcData as unknown as {
        success: boolean;
        users: UserActivityEntry[];
        total: number;
        error?: string;
      };

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch user activity');
      }

      setUsers(result.users || []);
      setTotal(result.total || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch user activity:', err);
      toast.error('Failed to load user activity');
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  return {
    users,
    total,
    page,
    pageSize,
    loading,
    fetch
  };
}

/**
 * Hook for fetching activity summary stats
 */
export function useActivitySummary(): UseActivitySummaryResult {
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Get DAU (users active in last 24h)
      const { count: dau } = await supabase
        .from('accounts')
        .select('*', { count: 'exact', head: true })
        .gte('last_active', yesterday.toISOString());

      // Get WAU (users active in last 7 days)
      const { count: wau } = await supabase
        .from('accounts')
        .select('*', { count: 'exact', head: true })
        .gte('last_active', lastWeek.toISOString());

      // Get MAU (users active in last 30 days)
      const { count: mau } = await supabase
        .from('accounts')
        .select('*', { count: 'exact', head: true })
        .gte('last_active', lastMonth.toISOString());

      // Get total reports in last 7 days (from reporting_run)
      const { count: totalReports } = await supabase
        .from('reporting_run')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', lastWeek.toISOString())
        .eq('status', 'completed');

      // Get unique users who generated reports in last 7 days
      const { data: activeUsers } = await supabase
        .from('reporting_run')
        .select('user_id')
        .gte('created_at', lastWeek.toISOString())
        .eq('status', 'completed');

      const uniqueActiveUsers = new Set((activeUsers || []).map(u => u.user_id)).size;
      const avgReportsPerUser = uniqueActiveUsers > 0 ? (totalReports || 0) / uniqueActiveUsers : 0;

      // Get total dictation minutes (approximate from accounts)
      const { data: accountsData } = await supabase
        .from('accounts')
        .select('dictation_minutes_used')
        .gte('last_active', lastWeek.toISOString());

      const totalDictationMinutes = (accountsData || []).reduce(
        (sum, acc) => sum + (acc.dictation_minutes_used || 0),
        0
      );

      setSummary({
        dau: dau || 0,
        wau: wau || 0,
        mau: mau || 0,
        total_reports_7d: totalReports || 0,
        total_dictation_minutes_7d: totalDictationMinutes,
        avg_reports_per_user: avgReportsPerUser
      });
    } catch (err) {
      console.error('Failed to fetch activity summary:', err);
      toast.error('Failed to load activity summary');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    summary,
    loading,
    fetch
  };
}

/**
 * Hook for fetching activity trend over time
 */
export function useActivityTrend(): UseActivityTrendResult {
  const [trend, setTrend] = useState<ActivityTrendPoint[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (days: number = 30) => {
    setLoading(true);
    try {
      // Generate date buckets
      const buckets: ActivityTrendPoint[] = [];
      const now = new Date();

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        buckets.push({
          date: dateStr,
          active_users: 0,
          reports: 0
        });
      }

      // Get report counts by day
      const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      const { data: reports } = await supabase
        .from('reporting_run')
        .select('created_at, user_id')
        .gte('created_at', startDate.toISOString())
        .eq('status', 'completed');

      // Aggregate by date
      const reportsByDate: Record<string, { reports: number; users: Set<string> }> = {};

      (reports || []).forEach(r => {
        const dateStr = new Date(r.created_at).toISOString().split('T')[0];
        if (!reportsByDate[dateStr]) {
          reportsByDate[dateStr] = { reports: 0, users: new Set() };
        }
        reportsByDate[dateStr].reports++;
        reportsByDate[dateStr].users.add(r.user_id);
      });

      // Merge into buckets
      buckets.forEach(bucket => {
        const data = reportsByDate[bucket.date];
        if (data) {
          bucket.reports = data.reports;
          bucket.active_users = data.users.size;
        }
      });

      setTrend(buckets);
    } catch (err) {
      console.error('Failed to fetch activity trend:', err);
      toast.error('Failed to load activity trend');
      setTrend([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    trend,
    loading,
    fetch
  };
}
